import { useState, useEffect } from "react";
import Button from "./forms/Button";
import ProfileHeaderSection from "./ProfileHeaderSection";
import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../context/globalContext";
import { deleteAuthUser } from "../helper";
import { useMarkAttendance, useGetLastAttendance } from "../hooks/useAuth";
import { useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import axios from "axios";

const Header = () => {
  const queryClient = useQueryClient();

  const [location, setLocation] = useState<{
    latitude: number | null;
    longitude: number | null;
  }>({ latitude: null, longitude: null });
  const [ipAddress, setIpAddress] = useState("");

  useEffect(() => {
    // Get IP address
    const getIpAddress = async () => {
      try {
        const response = await axios.get("https://api.ipify.org?format=json");
        setIpAddress(response.data.ip);
      } catch (error) {
        console.error("Unable to fetch IP address.");
      }
    };

    // Get location
    const getLocation = () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            });
          },
          (error) => {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                console.error("User denied the request for Geolocation.");
                break;
              case error.POSITION_UNAVAILABLE:
                console.error("Location information is unavailable.");
                break;
              case error.TIMEOUT:
                console.error("The request to get user location timed out.");
                break;
              default:
                console.error("An unknown error occurred.");
                break;
            }
          }
        );
      } else {
        console.error("Geolocation is not supported by this browser.");
      }
    };

    getIpAddress();
    getLocation();
  }, []);

  const payload = {
    ip: ipAddress,
    location: `${location?.latitude},${location?.longitude}`,
  };
  const { mutateMarkAttendance, isPending } = useMarkAttendance();
  const navigate = useNavigate();
  const { user } = useGlobalContext();

  const name = user?.firstname;
  const { lastAttendance } = useGetLastAttendance(Number(user?.id));

  const handleLogout = () => {
    deleteAuthUser();
    navigate("/sign-in");
  };
  function getGreeting() {
    const currentHour = new Date().getHours();
    if (currentHour < 12) {
      return "Good Morning";
    } else if (currentHour < 18) {
      return "Good Afternoon";
    } else {
      return "Good Evening";
    }
  }

  // Separate last check-in and check-out times
  const lastCheckIn =
    lastAttendance?.[0]?.action === "check-in"
      ? lastAttendance?.[0]?.created
      : null;
  const lastCheckOut =
    lastAttendance?.[1]?.action === "check-out"
      ? lastAttendance?.[1]?.created
      : null;

  const [isCheck, setIsCheck] = useState("check-in");

  useEffect(() => {
    if (lastAttendance?.length > 0) {
      const [lastCheck, secondLastCheck] = lastAttendance;

      if (
        lastCheck.action === "check-in" &&
        (!secondLastCheck ||
          new Date(lastCheck.created) > new Date(secondLastCheck.created))
      ) {
        setIsCheck("check-in");
      } else {
        setIsCheck("check-out");
      }
    } else {
      setIsCheck("check-out"); // Default state when there are no attendance
    }
  }, [lastAttendance]);

  const isChecked = isCheck === "check-in" ? "Check Out" : "Check In";

  const handleCheck = () => {
    const newCheck = isCheck === "check-in" ? "check-out" : "check-in";
    mutateMarkAttendance(
      { ...payload, user: Number(user?.id), checkType: newCheck },
      {
        onSuccess: (data) => {
          if (data?.data?.success) {
            queryClient.invalidateQueries({ queryKey: ["lastAttendance"] });
            setIsCheck(newCheck);
            return;
          }
        },
        onError: (error) => {
          console.log("Error", error);
        },
      }
    );
  };

  return (
    <div>
      <ProfileHeaderSection />
      <div className="bg-bloodRed rounded-tr-xl rounded-bl-xl lg:mt-2 mt-14 w-full">
        <div className="text-white px-8 py-3">
          <div>
            <p className={`md:text-3xl  text-left font-lato`}>Welcome back! </p>
            <p className={`md:text-2xl text-left font-lato`}>
              {getGreeting()} {name},
            </p>
          </div>
          <div className="flex flex-row w-full mx-auto justify-between mt-2">
            <div className="flex flex-col justify-start gap-2">
              <div>
                <Button
                  loading={isPending}
                  bg="white"
                  text={isChecked}
                  onClick={() => handleCheck()}
                  width={"28"}
                />
              </div>

              <p className={`text-left`}>
                Last Checked-In:{" "}
                {lastCheckIn
                  ? dayjs(lastCheckIn).format("MMM, D YYYY h:mma")
                  : "N/A"}
              </p>
              <p>
                Last Checked-Out:{" "}
                {lastCheckOut
                  ? dayjs(lastCheckOut).format("MMM, D YYYY h:mma")
                  : "N/A"}
              </p>
            </div>
            <div className="flex flex-row gap-2">
              <Button
                text="Log Out"
                onClick={handleLogout}
                bg="white"
                width={"28"}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Header;
